// 过关动画（作者 2026-11-02：“每一关进入下一关前有个动画，玩家从上一关坐船沿着地图线路图到下一关”）：
// 江湖山路图（SVG，作者 11-03 改武侠主题；原运河图），13 站 = 级数 2 → A；一副结算时两队的旗从上一站走到新站。动画关掉时直接停在新站。
// 类名沿用 boat / us / them（测试与样式在用），画的是小旗。
import { STATIONS } from './route.ts';

const NS = 'http://www.w3.org/2000/svg';
const el = <K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}) => {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
};
/** 蜿蜒的山路：从左下的山门到右上的论道台。 */
const RIVER = 'M 30 190 C 120 190, 140 80, 230 90 S 360 200, 450 170 S 560 60, 650 80 S 780 190, 860 140 S 940 50, 975 45';

function boat(color: string, text: string): SVGGElement {
  const g = el('g', { class: 'boat' });
  const inner = el('g', { transform: 'scale(1.6)' }); // 旗放大，旗面上的字看得清
  inner.append(
    el('ellipse', { cx: 0, cy: 14, rx: 9, ry: 3, fill: '#2a242033' }), // 旗杆落地的影子
    el('line', { x1: 0, y1: 14, x2: 0, y2: -30, stroke: '#5a4030', 'stroke-width': 2.5, 'stroke-linecap': 'round' }), // 旗杆
    el('circle', { cx: 0, cy: -31, r: 2.5, fill: '#c9a24a' }), // 杆顶金珠
    el('path', { d: 'M 1.5 -28 L 25 -25 Q 21 -16 25 -7 L 1.5 -4 Z', fill: color }), // 燕尾旗面
  );
  const t = el('text', { x: 12, y: -11, 'text-anchor': 'middle', 'font-size': 13, fill: '#fbf3e2', 'font-weight': 700 });
  t.textContent = text;
  inner.append(t);
  g.append(inner);
  return g;
}

/**
 * 画线路图并开船。from / to 是两队的级数（2–14）；animate=false 时直接停在终点。
 * 返回 Promise，在船到站后 resolve（测试用 data-done 判断）。
 */
export function drawRouteMap(box: HTMLElement, from: [number, number], to: [number, number], animate: boolean): Promise<void> {
  const svg = el('svg', { viewBox: '-44 -40 1064 320', class: 'routemap', role: 'img', 'aria-label': '江湖路' }); // 上方留出船的位置，下方留两排站名
  const narrow = box.clientWidth > 0 && box.clientWidth < 480;
  const river = el('path', { d: RIVER, fill: 'none', stroke: '#b9a88a', 'stroke-width': 16, 'stroke-linecap': 'round', opacity: 0.45 }); // 淡墨山道
  const bank = el('path', { d: RIVER, fill: 'none', stroke: '#5a4030', 'stroke-width': 2, 'stroke-dasharray': '2 9', 'stroke-linecap': 'round', opacity: 0.6 }); // 石阶脚印
  svg.append(river, bank);
  box.replaceChildren(svg);
  const len = (river as SVGPathElement).getTotalLength();
  const at = (lv: number) => (river as SVGPathElement).getPointAtLength((len * (lv - 2)) / 12);
  const keep = new Set([2, 14, ...from, ...to]);
  for (let lv = 2; lv <= 14; lv++) {
    const p = at(lv);
    const passed = lv <= Math.max(...to);
    svg.append(el('circle', { cx: p.x, cy: p.y, r: lv === 14 ? 11 : 7, fill: passed ? '#c9a24a' : '#fbf6ea', stroke: '#6a5a48', 'stroke-width': 2 }));
    if (narrow && !keep.has(lv)) continue; // 窄屏只标起点、终点、出发站、到达站
    // 站名都在河道下方、高低两排交错，船在河道上方，互不遮挡
    const label = el('text', { x: p.x, y: p.y + (lv % 2 === 0 ? 36 : 60), 'text-anchor': 'middle', 'font-size': narrow ? 34 : 24, fill: '#2a2420', 'font-weight': to.includes(lv) ? 700 : 400 });
    label.textContent = STATIONS[lv];
    svg.append(label);
  }
  const boats = [boat('#2f5d50', '我'), boat('#b8322a', '对')];
  boats[0].classList.add('us');
  boats[1].classList.add('them');
  svg.append(...boats);
  const place = (g: SVGGElement, lv: number, team: number) => {
    const p = at(lv);
    g.setAttribute('transform', `translate(${p.x + (team ? 20 : -20)} ${p.y - 26})`);
  };
  boats.forEach((g, t) => place(g, from[t], t));
  box.dataset.done = '0';
  return new Promise((resolve) => {
    const finish = () => { boats.forEach((g, t) => place(g, to[t], t)); box.dataset.done = '1'; resolve(); };
    if (!animate || (from[0] === to[0] && from[1] === to[1])) { finish(); return; }
    const t0 = performance.now(), dur = 1600;
    const tick = (now: number) => {
      const k = Math.min(1, (now - t0) / dur);
      const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2; // 缓进缓出
      boats.forEach((g, t) => {
        const lv = from[t] + (to[t] - from[t]) * e;
        const p = (river as SVGPathElement).getPointAtLength((len * (lv - 2)) / 12);
        g.setAttribute('transform', `translate(${p.x + (t ? 20 : -20)} ${p.y - 26 + Math.abs(Math.sin(now / 90)) * -3})`); // 走山路一步一颠
      });
      if (k < 1) requestAnimationFrame(tick); else finish();
    };
    requestAnimationFrame(tick);
  });
}
